import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { authenticatedPut, authenticatedPost, authenticatedDelete } from "../utils/apiUtils";
import { Industry, Skill, type Profile, type PrivacySettings } from "@/types/profile";
import { BasicInformation } from "@/components/profile/BasicInformation";
import { SocialLinks } from "@/components/profile/SocialLinks";
import { useProfile, getFullProfileImageUrl } from "../contexts/ProfileContext";
import { Avatar } from "@/components/ui/avatar";
import { Camera, Trash2, User, Shield, Bell, ChevronRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import TagsSection from '@/components/profile/TagsSection';

// Available sidebar sections
type SectionKey = "profile" | "privacy" | "notifications";

const EditProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile: contextProfile, loading: contextLoading, fetchProfile, updateProfileContext } = useProfile();
  const [loading, setLoading] = useState(false);
  const [originalProfile, setOriginalProfile] = useState<Profile | null>(null);
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<Industry[]>([]);
  const [profile, setProfile] = useState<Profile>({
    username: "",
    bio: "",
    contactInfo: "",
    showContactInfo: true,
    skills: [],
    industries: [],
    socialLinks: []
  });
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    id: "",
    userId: "",
    profileVisibility: "PUBLIC",
    showReviews: true,
    showWillHistory: true,
    showContactInfo: true
  });
  const [formData, setFormData] = useState<Profile>({
    username: "",
    bio: "",
    contactInfo: "",
    showContactInfo: true,
    skills: [],
    industries: [],
    socialLinks: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // New state for storing the selected profile picture file
  const [selectedProfilePicture, setSelectedProfilePicture] = useState<File | null>(null);
  // New state for tracking if the profile picture should be removed
  const [shouldRemoveProfilePicture, setShouldRemoveProfilePicture] = useState(false);
  // New state for temporary preview URL
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  // New state for active section
  const [activeSection, setActiveSection] = useState<SectionKey>("profile");

  const hasUnsavedChanges = Boolean(
    originalProfile && 
    (JSON.stringify(originalProfile) !== JSON.stringify({
      ...profile,
      skills: selectedSkills,
      industries: selectedIndustries,
      privacySettings: privacySettings
    })) || 
    selectedProfilePicture !== null || 
    shouldRemoveProfilePicture
  );

  useEffect(() => {
    const initializeProfile = async () => {
      try {
        setLoading(true);
        
        // Try to get profile from context first
        if (contextProfile) {
          setProfile(contextProfile);
          setOriginalProfile(contextProfile);
          setSelectedSkills(contextProfile.skills || []);
          setSelectedIndustries(contextProfile.industries || []);
          // Initialize privacy settings if available
          if (contextProfile.privacySettings) {
            setPrivacySettings(contextProfile.privacySettings);
          } else {
            // Initialize with defaults that match the direct showContactInfo property
            setPrivacySettings({
              id: "",
              userId: contextProfile.userId || "",
              profileVisibility: "PUBLIC",
              showReviews: true,
              showWillHistory: true,
              showContactInfo: contextProfile.showContactInfo ?? true
            });
          }
          setFormData({
            username: contextProfile.username || '',
            bio: contextProfile.bio || '',
            contactInfo: contextProfile.contactInfo || '',
            showContactInfo: contextProfile.showContactInfo || true,
            skills: contextProfile.skills || [],
            industries: contextProfile.industries || [],
            socialLinks: contextProfile.socialLinks || []
          });
        } else {
          // If not in context, fetch it
          const fetchedProfile = await fetchProfile();
          
          if (fetchedProfile) {
            setProfile(fetchedProfile);
            setOriginalProfile(fetchedProfile);
            setSelectedSkills(fetchedProfile.skills || []);
            setSelectedIndustries(fetchedProfile.industries || []);
            // Initialize privacy settings if available
            if (fetchedProfile.privacySettings) {
              setPrivacySettings(fetchedProfile.privacySettings);
            } else {
              // Initialize with defaults that match the direct showContactInfo property
              setPrivacySettings({
                id: "",
                userId: fetchedProfile.userId || "",
                profileVisibility: "PUBLIC",
                showReviews: true,
                showWillHistory: true,
                showContactInfo: fetchedProfile.showContactInfo ?? true
              });
            }
            setFormData({
              username: fetchedProfile.username || '',
              bio: fetchedProfile.bio || '',
              contactInfo: fetchedProfile.contactInfo || '',
              showContactInfo: fetchedProfile.showContactInfo || true,
              skills: fetchedProfile.skills || [],
              industries: fetchedProfile.industries || [],
              socialLinks: fetchedProfile.socialLinks || []
            });
          } else {
            // No profile exists yet, keep default empty state
            console.log('No existing profile found, will create new one on save');
          }
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
        toast.error("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    initializeProfile();
    
    // Cleanup the object URL when the component is unmounted
    return () => {
      if (profilePicturePreview) {
        URL.revokeObjectURL(profilePicturePreview);
      }
    };
  }, [contextProfile, fetchProfile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {      
      // Ensure skills and industries are properly typed 
      const typedSkills = selectedSkills.map(skill => skill as Skill);
      const typedIndustries = selectedIndustries.map(industry => industry as Industry);
      
      // Update profile state to ensure consistency before submitting
      // This ensures both skills and industries are properly reflected in the profile
      setProfile(prev => ({
        ...prev,
        skills: typedSkills,
        industries: typedIndustries
      }));
      
      // Ensure userId is set in privacySettings if available
      const updatedPrivacySettings = {
        ...privacySettings,
        userId: profile.userId || privacySettings.userId
      };
      
      // Prepare the form data with updated skills and industries
      const updatedFormData = {
        ...profile,
        skills: typedSkills,
        industries: typedIndustries,
        showContactInfo: updatedPrivacySettings.showContactInfo, // Keep both in sync
        privacySettings: updatedPrivacySettings
      };
      
      // If the profile has profilePictureUrl, map it to profileImageUrl for the API
      if (profile.profilePictureUrl) {
        updatedFormData.profileImageUrl = profile.profilePictureUrl;
        // Don't include profilePictureUrl in the request to avoid the error
        delete updatedFormData.profilePictureUrl;
      }
      
      // Log the complete data being sent for debugging
      console.log("Updating profile data with full payload:", JSON.stringify(updatedFormData, null, 2));
      console.log("Privacy settings being sent:", JSON.stringify(updatedFormData.privacySettings, null, 2));
      
      // Attempt to send privacy settings separately if needed
      let profileUpdateSuccess = false;
      let privacySettingsUpdateSuccess = false;
      
      // First, update the profile details
      const response = await authenticatedPut(
        `${import.meta.env.VITE_API_URL}/profiles/update`,
        updatedFormData
      );
      
      // Log the response
      console.log("Profile update response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to update profile:", response.status, errorText);
        throw new Error(`Failed to save profile: ${response.status} ${errorText}`);
      } else {
        profileUpdateSuccess = true;
        // Ensure we're working with the correct data format
        const responseData = await response.json();
        
        // Ensure skills and industries are properly set in the response
        const skills = responseData.skills || [];
        const industries = responseData.industries || [];
        
        // Update both state variables to ensure consistency
        setSelectedSkills(skills);
        setSelectedIndustries(industries);
        
        // Update the profile state with the full response data
        setProfile({
          ...responseData,
          // Ensure skills and industries are properly set in the profile state
          skills: skills,
          industries: industries
        });
        
        // Also check if the profile has been properly updated in the response
        if (!responseData.skills && skills.length > 0) {
          console.warn("Skills may not have been properly updated in the response");
        }
        if (!responseData.industries && industries.length > 0) {
          console.warn("Industries may not have been properly updated in the response");
        }
        
        // Check if privacy settings were included in the response
        if (responseData.privacySettings) {
          console.log("Privacy settings updated successfully via profile update:", responseData.privacySettings);
          setPrivacySettings(responseData.privacySettings);
          privacySettingsUpdateSuccess = true;
        } else {
          console.warn("Privacy settings not found in response, may need separate update");
        }
      }
      
      // If privacy settings weren't updated with the profile, try updating them separately
      if (profileUpdateSuccess && !privacySettingsUpdateSuccess) {
        try {
          console.log("Attempting to update privacy settings separately...");
          const privacyResponse = await authenticatedPut(
            `${import.meta.env.VITE_API_URL}/profiles/privacy-settings`,
            updatedPrivacySettings
          );
          
          if (!privacyResponse.ok) {
            console.warn("Failed to update privacy settings separately:", await privacyResponse.text());
          } else {
            console.log("Privacy settings updated separately");
            try {
              const privacyData = await privacyResponse.json();
              console.log("Privacy settings update response:", privacyData);
              if (privacyData) {
                setPrivacySettings(privacyData);
              }
            } catch (e) {
              console.log("Could not parse privacy settings response");
            }
          }
        } catch (error) {
          console.error("Error updating privacy settings separately:", error);
        }
      }
      
      let uploadSuccess = true;
      
      // If we need to remove the profile picture
      if (shouldRemoveProfilePicture && profile.profilePictureUrl) {
        console.log("Removing profile picture...");
        try {
          const deleteResponse = await authenticatedDelete(
            `${import.meta.env.VITE_API_URL}/profiles/profile-picture`
          );
          
          if (!deleteResponse.ok) {
            const errorText = await deleteResponse.text();
            console.error("Failed to remove profile picture:", deleteResponse.status, errorText);
            uploadSuccess = false;
            toast.error("Failed to remove profile picture");
          }
        } catch (error) {
          console.error("Error removing profile picture:", error);
          uploadSuccess = false;
          toast.error("Error removing profile picture");
        }
      }
      // If we have a new profile picture to upload
      else if (selectedProfilePicture) {
        console.log("Uploading new profile picture...");
        try {
          const formData = new FormData();
          formData.append('file', selectedProfilePicture);
          
          // Log what we're uploading
          console.log(`Uploading file: ${selectedProfilePicture.name}, type: ${selectedProfilePicture.type}, size: ${selectedProfilePicture.size}`);
          console.log("Upload URL:", `${import.meta.env.VITE_API_URL}/profiles/profile-picture`);
          
          const uploadResponse = await authenticatedPost(
            `${import.meta.env.VITE_API_URL}/profiles/profile-picture`,
            formData,
            true // skipJsonContentType - important for FormData
          );
          
          if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            console.error("Failed to upload profile picture:", uploadResponse.status, errorText);
            uploadSuccess = false;
            toast.error(`Failed to upload profile picture: ${uploadResponse.status}`);
          } else {
            console.log("Profile picture uploaded successfully");
            // Check if we got json data back
            try {
              const responseData = await uploadResponse.json();
              console.log("Upload response:", responseData);
              
              // Handle response data - the backend might return profileImageUrl
              if (responseData.profileImageUrl) {
                // Update our state with the new image URL
                profile.profileImageUrl = responseData.profileImageUrl;
                // Also set profilePictureUrl for frontend compatibility
                profile.profilePictureUrl = responseData.profileImageUrl;
              }
            } catch (e) {
              console.log("No JSON response from upload");
            }
          }
        } catch (error) {
          console.error("Error uploading profile picture:", error);
          uploadSuccess = false;
          toast.error(error instanceof Error ? `Error uploading: ${error.message}` : "Error uploading profile picture");
        }
      }
      
      // Update the final context update to remove the responseData reference
      // Update the context with our final state - include ALL profile data 
      const finalProfileData = {
        ...profile,           // Base profile with most fields
        skills: selectedSkills,
        industries: selectedIndustries,
        // Make sure privacy settings are included
        privacySettings: updatedPrivacySettings,
        // Ensure showContactInfo is synced
        showContactInfo: updatedPrivacySettings.showContactInfo
      };
      updateProfileContext(finalProfileData);
      
      if (uploadSuccess) {
        toast.success("Profile saved successfully!");
      } else {
        toast.warning("Profile saved but there was an issue with the profile picture");
      }
      
      navigate('/profile');
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error(error instanceof Error ? error.message : "Failed to save profile");
    } finally {
      setSubmitting(false);
    }
  };

  // Modified function to handle profile picture selection
  const handleProfilePictureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      toast.error("Please upload an image file (JPEG, PNG, GIF, or WEBP)");
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    // Create a preview URL for the selected file
    const previewUrl = URL.createObjectURL(file);
    
    // Store the file and preview URL
    setSelectedProfilePicture(file);
    setProfilePicturePreview(previewUrl);
    
    // Reset the remove flag if it was set
    setShouldRemoveProfilePicture(false);
    
    toast.success("Photo selected! Click 'Save Profile' to apply changes.");
  };

  // Modified function to handle profile picture removal
  const handleRemoveProfilePicture = () => {
    if (!(profile.profileImageUrl || profile.profilePictureUrl) && !profilePicturePreview) return;

    if (!confirm("Are you sure you want to remove your profile picture?")) {
      return;
    }

    // Clear the selected file if there is one
    if (selectedProfilePicture) {
      setSelectedProfilePicture(null);
    }
    
    // Clear the preview URL if there is one
    if (profilePicturePreview) {
      URL.revokeObjectURL(profilePicturePreview);
      setProfilePicturePreview(null);
    }
    
    // Mark that we should remove the existing profile picture on save
    if (profile.profileImageUrl || profile.profilePictureUrl) {
      setShouldRemoveProfilePicture(true);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    toast.success("Photo will be removed when you save your profile");
  };

  // Update privacy settings
  const handlePrivacyChange = (field: keyof PrivacySettings, value: any) => {
    // For profileVisibility, only accept 'PUBLIC' or 'PRIVATE'
    if (field === 'profileVisibility' && value !== 'PUBLIC' && value !== 'PRIVATE') {
      // Default to PUBLIC if an invalid value is provided
      value = 'PUBLIC';
    }
    
    setPrivacySettings(prev => ({ 
      ...prev, 
      [field]: value 
    }));
    
    // Also update the direct showContactInfo property for backwards compatibility
    if (field === 'showContactInfo') {
      setProfile(prev => ({ 
        ...prev, 
        showContactInfo: value 
      }));
    }
  };

  const showCancelButton = Boolean(originalProfile && originalProfile.username);
  const isLoading = loading || contextLoading;

  // Determine what image URL to use for the preview
  const profilePictureUrl = (profile.profileImageUrl || profile.profilePictureUrl) ? 
    getFullProfileImageUrl(profile.profileImageUrl || profile.profilePictureUrl) : undefined;
    
  const displayImageUrl = profilePicturePreview || 
    (!shouldRemoveProfilePicture ? profilePictureUrl : undefined);

  // Sidebar navigation items
  const sidebarItems = [
    { key: "profile" as SectionKey, label: "Profile Information", icon: <User className="h-5 w-5" /> },
    { key: "privacy" as SectionKey, label: "Privacy Settings", icon: <Shield className="h-5 w-5" /> },
    { key: "notifications" as SectionKey, label: "Notifications", icon: <Bell className="h-5 w-5" /> }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="container mx-auto px-4 pt-24 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-semibold mb-4">Settings</h2>
              <div className="space-y-1">
                {sidebarItems.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setActiveSection(item.key)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors",
                      activeSection === item.key
                        ? "bg-gray-100 text-gray-900 font-medium"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {item.icon}
                      <span>{item.label}</span>
                    </div>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            <div className="bg-white rounded-lg shadow p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Profile section */}
                {activeSection === "profile" && (
                  <div className="space-y-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-6">Edit Profile</h1>

                    {/* Profile Picture Section */}
                    <div className="mb-8 flex flex-col items-center">
                      <div className="relative group">
                        <Avatar className="h-32 w-32 rounded-full border-2 border-gray-200">
                          {displayImageUrl ? (
                            <img 
                              src={displayImageUrl} 
                              alt={profile.username} 
                              className="h-full w-full object-cover rounded-full"
                            />
                          ) : (
                            <div className="bg-gradient-to-br from-purple-500 to-pink-600 h-full w-full flex items-center justify-center text-white text-3xl font-bold">
                              {profile.username?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                        </Avatar>
                        
                        <div className="absolute inset-0 bg-black bg-opacity-40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                          <div className="text-white flex flex-col items-center">
                            <Camera className="h-6 w-6 mb-1" />
                            <span className="text-xs">Change Photo</span>
                          </div>
                          <input 
                            type="file" 
                            ref={fileInputRef}
                            accept="image/*" 
                            className="absolute inset-0 opacity-0 cursor-pointer" 
                            onChange={handleProfilePictureUpload}
                            disabled={submitting}
                          />
                        </div>
                      </div>
                      
                      {(displayImageUrl || selectedProfilePicture) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="mt-2 text-red-500 hover:text-red-700 flex items-center gap-1"
                          onClick={handleRemoveProfilePicture}
                          disabled={submitting}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span>Remove Photo</span>
                        </Button>
                      )}
                      
                      {selectedProfilePicture && (
                        <p className="text-sm text-emerald-600 mt-2">
                          New photo selected (will be uploaded when you save)
                        </p>
                      )}
                      
                      {shouldRemoveProfilePicture && (
                        <p className="text-sm text-red-600 mt-2">
                          Photo will be removed when you save
                        </p>
                      )}
                    </div>

                    <BasicInformation 
                      profile={profile}
                      onProfileChange={setProfile}
                    />

                    <TagsSection
                      selectedSkills={selectedSkills}
                      selectedIndustries={selectedIndustries}
                      onSkillsChange={(skills) => {
                        console.log("Skills updated in EditProfile:", skills);
                        setSelectedSkills(skills);
                        // Also update the profile state to ensure UI consistency
                        setProfile(prev => ({ ...prev, skills: skills }));
                      }}
                      onIndustriesChange={(industries) => {
                        console.log("Industries updated in EditProfile:", industries);
                        setSelectedIndustries(industries);
                        // Also update the profile state to ensure UI consistency
                        setProfile(prev => ({ ...prev, industries: industries }));
                      }}
                    />

                    <SocialLinks
                      socialLinks={profile.socialLinks}
                      onSocialLinksChange={(newLinks) => 
                        setProfile(prev => ({ ...prev, socialLinks: newLinks }))
                      }
                    />
                  </div>
                )}

                {/* Privacy Settings section */}
                {activeSection === "privacy" && (
                  <div className="space-y-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-6">Privacy Settings</h1>
                    
                    <div className="space-y-6">
                      <div className="space-y-3">
                        <h3 className="text-lg font-medium">Profile Visibility</h3>
                        <p className="text-sm text-gray-500">
                          Control who can see your profile information
                        </p>
                        <RadioGroup 
                          value={privacySettings.profileVisibility} 
                          onValueChange={(value) => handlePrivacyChange('profileVisibility', value)}
                          className="space-y-2"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="PUBLIC" id="visibility-public" />
                            <Label htmlFor="visibility-public">Public - Anyone can view your profile</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="PRIVATE" id="visibility-private" />
                            <Label htmlFor="visibility-private">Private - Only you can view your profile</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      <Separator />

                      <div className="space-y-4">
                        <h3 className="text-lg font-medium">Content Visibility</h3>
                        <p className="text-sm text-gray-500">
                          Control which parts of your profile are visible to others
                        </p>
                        
                        <div className="space-y-3">
                          <div className="flex items-center justify-between space-x-2">
                            <Label htmlFor="showContactInfo">Show contact information</Label>
                            <Switch
                              id="showContactInfo"
                              checked={privacySettings.showContactInfo}
                              onCheckedChange={(checked) => handlePrivacyChange('showContactInfo', checked)}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between space-x-2">
                            <Label htmlFor="showReviews">Show reviews on your profile</Label>
                            <Switch
                              id="showReviews"
                              checked={privacySettings.showReviews}
                              onCheckedChange={(checked) => handlePrivacyChange('showReviews', checked)}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between space-x-2">
                            <Label htmlFor="showWillHistory">Show wants history on your profile</Label>
                            <Switch
                              id="showWillHistory"
                              checked={privacySettings.showWillHistory}
                              onCheckedChange={(checked) => handlePrivacyChange('showWillHistory', checked)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notifications section - placeholder for future implementation */}
                {activeSection === "notifications" && (
                  <div className="space-y-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-6">Notification Settings</h1>
                    
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                      <p className="text-gray-600 text-center">
                        Notification settings will be available in a future update.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-4 mt-8">
                  {showCancelButton && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/profile')}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={isLoading || (Boolean(!hasUnsavedChanges) && Boolean(showCancelButton))}
                  >
                    {submitting ? "Saving..." : isLoading ? "Loading..." : "Save Profile"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EditProfile;
